import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth, dashboardPath } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import Home from "@/pages/Home";
import Login from "@/pages/Login";
import RegisterClient from "@/pages/RegisterClient";
import RegisterContractor from "@/pages/RegisterContractor";
import ClientDashboard from "@/pages/client/ClientDashboard";
import Properties from "@/pages/client/Properties";
import PropertyDetail from "@/pages/client/PropertyDetail";
import RequestService from "@/pages/client/RequestService";
import PublicRequestService from "@/pages/PublicRequestService";
import PublicPlans from "@/pages/PublicPlans";
import Estimates from "@/pages/client/Estimates";
import Invoices from "@/pages/client/Invoices";
import WorkOrdersList from "@/pages/WorkOrdersList";
import WorkOrderDetail from "@/pages/WorkOrderDetail";
import { ContractorDashboard, ContractorAvailable } from "@/pages/contractor/ContractorDashboard";
import { ContractorEarnings, ContractorProfile } from "@/pages/contractor/ContractorExtras";
import DispatchDashboard from "@/pages/dispatch/DispatchDashboard";
import DispatchWorkOrders from "@/pages/dispatch/DispatchWorkOrders";
import Contractors from "@/pages/dispatch/Contractors";
import AdminContractorProfile from "@/pages/dispatch/ContractorProfile";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import PaymentResult from "@/pages/PaymentResult";
import PublicPay from "@/pages/PublicPay";
import CalendarPage from "@/pages/CalendarPage";
import Plans from "@/pages/client/Plans";
import Recurring from "@/pages/Recurring";
import Turnovers from "@/pages/Turnovers";
import ContractorPerformance from "@/pages/admin/ContractorPerformance";
import Reports from "@/pages/admin/Reports";
import DocumentsExpiring from "@/pages/admin/DocumentsExpiring";
import CRM from "@/pages/admin/CRM";
import Markets from "@/pages/admin/Markets";
import EmailSettings from "@/pages/admin/EmailSettings";
import EmailQueue from "@/pages/admin/EmailQueue";
import NotificationAdmin from "@/pages/admin/NotificationAdmin";
import DispatchSettings from "@/pages/admin/DispatchSettings";
import SmsSettings from "@/pages/admin/SmsSettings";
import QuickBooksSettings from "@/pages/admin/QuickBooksSettings";
import QuickBooksSync from "@/pages/admin/QuickBooksSync";
import InvoiceCenter from "@/pages/admin/InvoiceCenter";
import PayoutCenter from "@/pages/admin/PayoutCenter";
import UploadReview from "@/pages/admin/UploadReview";
import ServiceRequests from "@/pages/admin/ServiceRequests";
import ContractorPayouts from "@/pages/contractor/ContractorPayouts";
import NotificationPreferences from "@/pages/NotificationPreferences";
import TenantAccess from "@/pages/tenant/TenantAccess";
import TenantHome from "@/pages/tenant/TenantHome";
import TenantRequestForm from "@/pages/tenant/TenantRequestForm";
import TenantRequestDetail from "@/pages/tenant/TenantRequestDetail";
import Team from "@/pages/client/Team";

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={dashboardPath(user.role)} replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/request-service" element={<PublicRequestService />} />
      <Route path="/plans" element={<PublicPlans />} />
      <Route path="/pay/:token" element={<PublicPay />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register/client" element={<RegisterClient />} />
      <Route path="/register/contractor" element={<RegisterContractor />} />

      <Route path="/app/client" element={<Protected roles={["client"]}><ClientDashboard /></Protected>} />
      <Route path="/app/client/properties" element={<Protected roles={["client"]}><Properties /></Protected>} />
      <Route path="/app/client/properties/:id" element={<Protected roles={["client"]}><PropertyDetail /></Protected>} />
      <Route path="/app/client/request" element={<Protected roles={["client"]}><RequestService /></Protected>} />
      <Route path="/app/client/work-orders" element={<Protected roles={["client"]}><WorkOrdersList /></Protected>} />
      <Route path="/app/client/estimates" element={<Protected roles={["client"]}><Estimates /></Protected>} />
      <Route path="/app/client/invoices" element={<Protected roles={["client"]}><Invoices /></Protected>} />

      <Route path="/app/client/plans" element={<Protected roles={["client"]}><Plans /></Protected>} />
      <Route path="/app/client/team" element={<Protected roles={["client"]}><Team /></Protected>} />
      <Route path="/app/dispatch/crm" element={<Protected roles={["dispatcher", "admin"]}><CRM /></Protected>} />
      <Route path="/app/admin/markets" element={<Protected roles={["admin"]}><Markets /></Protected>} />
      <Route path="/app/admin/email" element={<Protected roles={["admin"]}><EmailSettings /></Protected>} />
      <Route path="/app/admin/email-queue" element={<Protected roles={["admin"]}><EmailQueue /></Protected>} />
      <Route path="/app/admin/sms" element={<Protected roles={["admin"]}><SmsSettings /></Protected>} />
      <Route path="/app/admin/quickbooks" element={<Protected roles={["admin"]}><QuickBooksSettings /></Protected>} />
      <Route path="/app/admin/quickbooks/sync" element={<Protected roles={["admin"]}><QuickBooksSync /></Protected>} />
      <Route path="/app/admin/notifications" element={<Protected roles={["admin"]}><NotificationAdmin /></Protected>} />
      <Route path="/app/admin/dispatch" element={<Protected roles={["dispatcher", "admin"]}><DispatchSettings /></Protected>} />
      <Route path="/tenant/access" element={<TenantAccess />} />
      <Route path="/app/notifications" element={<Protected roles={["client", "contractor", "tenant"]}><NotificationPreferences /></Protected>} />
      <Route path="/app/tenant" element={<Protected roles={["tenant"]}><TenantHome /></Protected>} />
      <Route path="/app/tenant/new" element={<Protected roles={["tenant"]}><TenantRequestForm /></Protected>} />
      <Route path="/app/tenant/requests/:id" element={<Protected roles={["tenant"]}><TenantRequestDetail /></Protected>} />

      <Route path="/app/schedule" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/app/recurring" element={<Protected roles={["client", "dispatcher", "admin"]}><Recurring /></Protected>} />
      <Route path="/app/turnovers" element={<Protected roles={["client", "dispatcher", "admin"]}><Turnovers /></Protected>} />

      <Route path="/app/admin/performance" element={<Protected roles={["dispatcher", "admin"]}><ContractorPerformance /></Protected>} />
      <Route path="/app/admin/documents" element={<Protected roles={["dispatcher", "admin"]}><DocumentsExpiring /></Protected>} />
      <Route path="/app/admin/reports" element={<Protected roles={["admin"]}><Reports /></Protected>} />
      <Route path="/app/admin/invoices" element={<Protected roles={["dispatcher", "admin"]}><InvoiceCenter /></Protected>} />
      <Route path="/app/admin/contractor-payouts" element={<Protected roles={["dispatcher", "admin"]}><PayoutCenter /></Protected>} />
      <Route path="/app/admin/upload-review" element={<Protected roles={["dispatcher", "admin"]}><UploadReview /></Protected>} />
      <Route path="/app/admin/service-requests" element={<Protected roles={["dispatcher", "admin"]}><ServiceRequests /></Protected>} />
      <Route path="/app/contractor/payouts" element={<Protected roles={["contractor"]}><ContractorPayouts /></Protected>} />

      <Route path="/app/contractor" element={<Protected roles={["contractor"]}><ContractorDashboard /></Protected>} />
      <Route path="/app/contractor/available" element={<Protected roles={["contractor"]}><ContractorAvailable /></Protected>} />
      <Route path="/app/contractor/earnings" element={<Protected roles={["contractor"]}><ContractorEarnings /></Protected>} />
      <Route path="/app/contractor/profile" element={<Protected roles={["contractor"]}><ContractorProfile /></Protected>} />

      <Route path="/app/dispatch" element={<Protected roles={["dispatcher", "admin"]}><DispatchDashboard /></Protected>} />
      <Route path="/app/dispatch/work-orders" element={<Protected roles={["dispatcher", "admin"]}><DispatchWorkOrders /></Protected>} />
      <Route path="/app/dispatch/contractors" element={<Protected roles={["dispatcher", "admin"]}><Contractors /></Protected>} />
      <Route path="/app/dispatch/contractors/:id" element={<Protected roles={["dispatcher", "admin"]}><AdminContractorProfile /></Protected>} />

      <Route path="/app/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />

      <Route path="/app/wo/:id" element={<Protected><WorkOrderDetail /></Protected>} />

      <Route path="/payment/success" element={<PaymentResult />} />
      <Route path="/payment/cancel" element={<PaymentResult cancelled />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
